import { MOCK_GUIDELINES } from '../mock/accuracyMockData';
import { MOCK_JOBS } from '../mock/accuracyMockData';
import type { AccuracyScopeMetadata } from './accuracyScopeMetadata';
import type { SavedAccuracyReport } from '../types/accuracy';

const GUIDELINE_DISPLAY_NAMES: Record<string, string> = Object.fromEntries(
  MOCK_GUIDELINES.map((g) => [g.id, g.name])
);

export interface GuidelineRef {
  guideline_id: string;
  version: number;
}

export function getGuidelineDisplayName(guidelineId: string): string {
  return GUIDELINE_DISPLAY_NAMES[guidelineId] ?? guidelineId.slice(0, 8);
}

export function guidelineRefKey(ref: GuidelineRef): string {
  return `${ref.guideline_id}:${ref.version}`;
}

export function parseGuidelineRefKey(key: string): GuidelineRef {
  const sep = key.lastIndexOf(':');
  if (sep <= 0) {
    return { guideline_id: key, version: 0 };
  }
  return {
    guideline_id: key.slice(0, sep),
    version: Number(key.slice(sep + 1)),
  };
}

export function formatGuidelineWithVersion(ref: GuidelineRef): string {
  return `${getGuidelineDisplayName(ref.guideline_id)} v${ref.version}`;
}

/** Unique guideline + version pairs used by this report (filters + selected jobs). */
export function getReportGuidelineRefs(
  report: SavedAccuracyReport,
  metadata?: AccuracyScopeMetadata | null
): GuidelineRef[] {
  const f = report.filters;
  const byKey = new Map<string, GuidelineRef>();

  const add = (guideline_id: string, version: number) => {
    const key = guidelineRefKey({ guideline_id, version });
    byKey.set(key, { guideline_id, version });
  };

  if (f.guideline_id != null && f.guideline_version != null) {
    add(f.guideline_id, f.guideline_version);
  }

  for (const jobId of f.job_ids) {
    const apiJob = metadata?.jobsById.get(jobId);
    if (apiJob?.guideline_id && apiJob.version) {
      add(apiJob.guideline_id, apiJob.version);
      continue;
    }
    const job = MOCK_JOBS.find((j) => j.id === jobId);
    if (job) {
      add(job.guideline_id, job.version);
    }
  }

  return Array.from(byKey.values()).sort((a, b) => {
    const nameCmp = getGuidelineDisplayName(a.guideline_id).localeCompare(
      getGuidelineDisplayName(b.guideline_id),
      'ja'
    );
    if (nameCmp !== 0) return nameCmp;
    return b.version - a.version;
  });
}

export function formatReportGuidelines(report: SavedAccuracyReport): string {
  return getReportGuidelineRefs(report).map(formatGuidelineWithVersion).join('、');
}
